import React from 'react';

import {
    getNextHighlightedOptionIndex,
    getSelectableOptionIndex,
} from '@/caregiverSpace/shared/lib';
import { DropDownOption } from '@/caregiverSpace/shared/model';

type UseSelectKeyboardHandlersProps<TOption> = {
    isFocused: boolean;
    filteredOptions: DropDownOption<TOption>[];
    highlightedOptionIndex: number;
    setHighlightedOptionIndex: (index: number) => void;
    setIsFocused: (focused: boolean) => void;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onOptionSelect: (option: DropDownOption<TOption>) => void;
};

export const useSelectKeyboardHandlers = <TOption>({
    isFocused,
    filteredOptions,
    highlightedOptionIndex,
    setHighlightedOptionIndex,
    setIsFocused,
    inputRef,
    onOptionSelect,
}: UseSelectKeyboardHandlersProps<TOption>) => {
    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isFocused) {
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            setIsFocused(false);
            inputRef.current?.blur();
            return;
        }

        if (filteredOptions.length === 0) {
            return;
        }

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();

            const nextIndex = getNextHighlightedOptionIndex(
                highlightedOptionIndex,
                filteredOptions.length,
                e.key === 'ArrowDown' ? 'down' : 'up',
            );

            const validatedIndex = Math.max(
                0,
                Math.min(nextIndex, filteredOptions.length - 1),
            );

            setHighlightedOptionIndex(validatedIndex);
            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();

            const optionIndex = getSelectableOptionIndex(
                highlightedOptionIndex,
                filteredOptions.length,
            );

            const optionToSelect = filteredOptions[optionIndex];

            if (optionToSelect) {
                onOptionSelect(optionToSelect);
            }
        }
    };

    return { onKeyDownHandler };
};
